import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaRupeeSign,
  FaClock,
} from "react-icons/fa";
import DriverLayout from "../../layouts/DriverLayout";
import { getBookingHistory, reset } from "../../features/driver/driverSlice";

const RideHistory = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { driver, bookingHistory, loading, error } = useSelector(
    (state) => state.driver,
  );
  const [filter, setFilter] = useState("completed");

  useEffect(() => {
    if (driver && driver._id) {
      dispatch(getBookingHistory(driver._id));
    }

    return () => {
      dispatch(reset());
    };
  }, [dispatch, driver]);


  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(reset());
    }
  }, [error, dispatch]);



  const filteredBookings = () => {
    let filtered;

    if (filter === "all") {
      // Show only completed and cancelled rides for data integrity
      filtered = bookingHistory.filter((booking) =>
        booking.status === "completed" || booking.status === "cancelled"
      );
    } else if (filter === "current") {
      // Show current/active bookings (not completed or cancelled)
      filtered = bookingHistory.filter((booking) =>
        booking.status === "inProgress" ||
        booking.status === "assigned" ||
        booking.status === "confirmed"
      );
    } else {
      filtered = bookingHistory.filter((booking) => booking.status === filter);
    }

    // Sort bookings to show completed rides first, then by date
    // Use slice() to create a copy before sorting to avoid mutation error
    return [...filtered].sort((a, b) => {
      // Define priority order for statuses - prioritize completed rides
      const statusPriority = {
        'completed': 1,
        'cancelled': 2,
        'inProgress': 3,
        'assigned': 4,
        'confirmed': 5
      };

      // Get priority for each booking status
      const priorityA = statusPriority[a.status] || 6;
      const priorityB = statusPriority[b.status] || 6;

      // If priorities are different, sort by priority
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // If same priority, sort by date (newest first)
      const dateA = new Date(a.createdAt || a.pickupDate);
      const dateB = new Date(b.createdAt || b.pickupDate);
      return dateB - dateA;
    });
  };

  // Safety function to ensure we don't render objects as React children
  const safeRender = (value, fallback = "N/A") => {
    if (value === null || value === undefined) return fallback;

    if (typeof value === "object") {
      // If it's a date object, format it
      if (value instanceof Date) {
        return formatDateTime(value);
      }

      // If it's an array, join it or use fallback
      if (Array.isArray(value)) {
        return fallback;
      }

      // Special handling for rating objects with createdAt
      if (value && typeof value === 'object' && 'createdAt' in value) {
        // If it's a rating object, try to extract the rating value
        if (value.rating !== undefined) {
          return String(value.rating);
        }
        // If it's a rating object with value property
        if (value.value !== undefined) {
          return String(value.value);
        }
        // If it's a comment object, extract the comment
        if (value.comment !== undefined) {
          return String(value.comment);
        }
        return fallback;
      }

      return fallback;
    }

    // Ensure we always return a string
    return String(value);
  };

  // Helper function to safely extract rating value from rating objects
  const safeGetRating = (ratingObj) => {
    if (typeof ratingObj === 'number') return ratingObj;
    if (typeof ratingObj === 'object' && ratingObj !== null) {
      return ratingObj.rating || ratingObj.value || 0;
    }
    return 0;
  };

  // Helper function to safely extract comment from rating objects
  const safeGetComment = (ratingObj) => {
    if (typeof ratingObj === 'string') return ratingObj;
    if (typeof ratingObj === 'object' && ratingObj !== null) {
      return ratingObj.comment || ratingObj.text || '';
    }
    return '';
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 border border-green-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border border-red-200";
      case "inProgress":
        return "bg-blue-500 text-white border border-blue-600 shadow-md animate-pulse";
      case "assigned":
        return "bg-orange-500 text-white border border-orange-600 shadow-md";
      case "confirmed":
        return "bg-purple-500 text-white border border-purple-600 shadow-md";
      default:
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Error formatting datetime:", error);
      return "Invalid Date";
    }
  };

  if (loading && bookingHistory.length === 0) {
    return (
      <DriverLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
        </div>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout>
      <div className="container mx-auto">

        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold mb-2">Ride History</h1>
            {bookingHistory.length > 0 && (
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span>
                  Current: {bookingHistory.filter(b =>
                    b.status === "inProgress" || b.status === "assigned" || b.status === "confirmed"
                  ).length}
                </span>
                <span>
                  Completed: {bookingHistory.filter(b => b.status === "completed").length}
                </span>
                <span>
                  Total: {bookingHistory.length}
                </span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                filter === "all"
                  ? "bg-primary text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter("current")}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                filter === "current"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Current
            </button>
            <button
              onClick={() => setFilter("completed")}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                filter === "completed"
                  ? "bg-green-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Completed
            </button>
            <button
              onClick={() => setFilter("cancelled")}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                filter === "cancelled"
                  ? "bg-red-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Cancelled
            </button>
          </div>
        </div>

        {bookingHistory.length > 0 ? (
          <div className="space-y-4">
            {filteredBookings().map((booking) => {
              const isCurrentBooking = booking.status === "inProgress" ||
                                      booking.status === "assigned" ||
                                      booking.status === "confirmed";

              return (
              <div
                key={booking._id}
                className={`rounded-lg shadow-md overflow-hidden transition-all duration-200 ${
                  isCurrentBooking
                    ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-lg"
                    : "bg-white"
                }`}
              >
                <div className="p-4 border-b">
                  <div className="flex flex-col md:flex-row md:items-center justify-between">
                    <div className="flex items-center mb-2 md:mb-0">
                      <div className="mr-3">
                        <span className="text-xs font-semibold">
                          Booking ID
                        </span>
                        <p className="font-medium">{safeRender(booking.bookingId)}</p>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(booking.status)}`}
                      >
                        {booking.status.charAt(0).toUpperCase() +
                          booking.status.slice(1)}
                      </span>
                    </div>

                    <div className="flex items-center">
                      <div className="flex items-center mr-4">
                        <FaCalendarAlt className="text-gray-400 mr-1" />
                        <span className="text-sm">
                          {formatDate(booking.pickupDate || booking.createdAt)}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <FaRupeeSign className="text-gray-400 mr-1" />
                        <span className="text-sm font-semibold">
                          {booking.totalAmount ? `₹${booking.totalAmount.toFixed(2)}` : "Amount not available"}
                        </span>
                        <span className="text-xs text-gray-500 ml-2 capitalize">
                          ({booking.paymentMethod === "COD" ? "Cash" : "Online"})
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                            <FaMapMarkerAlt className="text-green-500" />
                          </div>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-gray-500">Pickup</p>
                          <p className="font-medium">
                            {booking.pickupLocation?.name ||
                              booking.from ||
                              "Unknown Location"}
                          </p>
                          <p className="text-sm">{safeRender(booking.pickupAddress)}</p>
                        </div>
                      </div>

                      <div className="flex">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                            <FaMapMarkerAlt className="text-red-500" />
                          </div>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-gray-500">Drop</p>
                          <p className="font-medium">
                            {booking.dropLocation?.name ||
                              booking.to ||
                              "Unknown Location"}
                          </p>
                          <p className="text-sm">{safeRender(booking.dropAddress)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <FaClock className="text-blue-500" />
                          </div>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-gray-500">Journey Time</p>
                          <p className="font-medium">{safeRender(booking.pickupTime)}</p>
                          <div className="text-sm space-y-1 mt-1">
                            {booking.journeyType === "roundTrip" && booking.returnDate ? (
                              <p>
                                <span className="text-gray-500">Return:</span>{" "}
                                {formatDate(booking.returnDate)}
                                {booking.returnTime && ` at ${booking.returnTime}`}
                              </p>
                            ) : (
                              <p className="text-gray-600">One Way</p>
                            )}
                            <p>
                              <span className="text-gray-500">Service:</span>{" "}
                              <span className="capitalize font-medium">
                                {booking.serviceType === "rental" ? "Local Rental" :
                                 booking.serviceType === "airport" ? "Airport Transfer" :
                                 booking.serviceType || "Outstation"}
                              </span>
                            </p>
                            {booking.viaCities && booking.viaCities.length > 0 && (
                              <p>
                                <span className="text-gray-500">Via:</span>{" "}
                                <span className="font-medium">
                                  {booking.viaCities.join(", ")}
                                </span>
                              </p>
                            )}
                            {booking.serviceType === "rental" && (
                              <div>
                                {booking.packageName && (
                                  <p>
                                    <span className="text-gray-500">Package:</span>{" "}
                                    <span className="font-medium">{booking.packageName}</span>
                                  </p>
                                )}
                                {booking.rentalHours && (
                                  <p>
                                    <span className="text-gray-500">Duration:</span>{" "}
                                    <span className="font-medium">{booking.rentalHours}h</span>
                                  </p>
                                )}
                              </div>
                            )}
                            {booking.serviceType === "airport" && (
                              <div>
                                {booking.airportCode && (
                                  <p>
                                    <span className="text-gray-500">Airport:</span>{" "}
                                    <span className="font-medium">{booking.airportCode}</span>
                                  </p>
                                )}
                                {booking.transferDirection && (
                                  <p>
                                    <span className="text-gray-500">Transfer:</span>{" "}
                                    <span className="capitalize font-medium">
                                      {booking.transferDirection === "to" ? "To Airport" : "From Airport"}
                                    </span>
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                            <span className="text-purple-500 font-medium">
                              P
                            </span>
                          </div>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-gray-500">Passenger</p>
                          <p className="font-medium">
                            {safeRender(booking.passengerDetails?.name)}
                          </p>
                          <p className="text-sm">
                            {safeRender(booking.passengerDetails?.phone)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {booking.completedAt && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <div className="flex justify-between items-center text-sm text-gray-500">
                        <span>
                          Completed on:{" "}
                          {formatDateTime(booking.completedAt)}
                        </span>
                        {booking.distance && (
                          <span>Distance: {safeRender(booking.distance)} km</span>
                        )}
                      </div>

                      {/* User Rating Display */}
                      {(() => {
                        const hasUserRating = safeGetRating(booking.userRating) > 0 ||
                                              safeGetRating(booking.ratings?.user?.rating) > 0 ||
                                              safeGetRating(booking.ratings?.user) > 0;


                        return hasUserRating ? (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-blue-800">
                              User Rating for You:
                            </span>
                            <div className="flex items-center">
                              <div className="flex text-yellow-500 mr-2">
                                {[...Array(5)].map((_, i) => {
                                  const rating = safeGetRating(
                                    booking.userRating ||
                                    booking.ratings?.user?.rating ||
                                    booking.ratings?.user ||
                                    0
                                  );
                                  return (
                                    <span
                                      key={i}
                                      className={
                                        i < rating
                                          ? "text-yellow-500"
                                          : "text-gray-300"
                                      }
                                    >
                                      ★
                                    </span>
                                  );
                                })}
                              </div>
                              <span className="text-sm text-gray-600">
                                (
                                {safeGetRating(
                                  booking.userRating ||
                                  booking.ratings?.user?.rating ||
                                  booking.ratings?.user ||
                                  0
                                )}
                                /5)
                              </span>
                            </div>
                          </div>
                          {(safeGetComment(booking.userRating) ||
                            safeGetComment(booking.ratings?.user?.comment) ||
                            safeGetComment(booking.ratings?.user) ||
                            safeGetComment(booking.userComment)) && (
                            <p className="text-sm text-gray-700 mt-2 italic">
                              "
                              {safeGetComment(
                                booking.userRating ||
                                booking.ratings?.user?.comment ||
                                booking.ratings?.user ||
                                booking.userComment
                              )}
                              "
                            </p>
                          )}
                        </div>
                        ) : null;
                      })()}

                      {/* Driver's Rating for User Display */}
                      {(() => {
                        const hasDriverRating = safeGetRating(booking.driverRating) > 0 ||
                                               safeGetRating(booking.ratings?.driver?.rating) > 0 ||
                                               safeGetRating(booking.ratings?.driver) > 0;


                        return hasDriverRating ? (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-yellow-800">
                              Your Rating for User:
                            </span>
                            <div className="flex items-center">
                              <div className="flex text-yellow-500 mr-2">
                                {[...Array(5)].map((_, i) => {
                                  const rating = safeGetRating(
                                    booking.driverRating ||
                                    booking.ratings?.driver?.rating ||
                                    booking.ratings?.driver ||
                                    0
                                  );
                                  return (
                                    <span
                                      key={i}
                                      className={
                                        i < rating
                                          ? "text-yellow-500"
                                          : "text-gray-300"
                                      }
                                    >
                                      ★
                                    </span>
                                  );
                                })}
                              </div>
                              <span className="text-sm text-gray-600">
                                (
                                {safeGetRating(
                                  booking.driverRating ||
                                  booking.ratings?.driver?.rating ||
                                  booking.ratings?.driver ||
                                  0
                                )}
                                /5)
                              </span>
                            </div>
                          </div>
                          {(safeGetComment(booking.driverRating) ||
                            safeGetComment(booking.ratings?.driver?.comment) ||
                            safeGetComment(booking.ratings?.driver)) && (
                            <p className="text-sm text-gray-700 mt-2 italic">
                              "
                              {safeGetComment(
                                booking.driverRating ||
                                booking.ratings?.driver?.comment ||
                                booking.ratings?.driver
                              )}
                              "
                            </p>
                          )}
                        </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FaCalendarAlt className="text-gray-400 text-3xl" />
            </div>
            <h2 className="text-xl font-semibold mb-2">No Ride History</h2>
            <p className="text-gray-600">
              You haven't completed any rides yet.
            </p>
          </div>
        )}
      </div>
    </DriverLayout>
  );
};

export default RideHistory;
